import React, { useState, useRef } from "react";
import { Modal, Input, List, Space, Tag, Button, message } from "antd";

const WriteOffModal = ({
  visible,
  onCancel,
  onSubmit,
  selectedStockItems,
  loading,
}) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      // Convert files to base64
      const documents = await Promise.all(
        uploadedFiles.map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve({
                base64FileString: reader.result,
                originalFileName: file.name,
              });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      // Prepare stock items
      const stockItems = selectedStockItems.flatMap((record) =>
        record.items.map((item) => ({ id: item.stockItemId }))
      );

      const payload = {
        stockItems: stockItems,
        documents: documents,
      };

      await onSubmit(payload);
      
      // Reset state after successful submission
      setUploadedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      message.error("Failed to process files");
      throw error;
    }
  };

  const handleCancel = () => {
    setUploadedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onCancel();
  };

  // Reset files when modal opens/closes
  React.useEffect(() => {
    if (!visible) {
      setUploadedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [visible]);

  return (
    <Modal
      title="Writeoff Stock Items"
      visible={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText="Submit"
      cancelText="Cancel"
      width={800}
      confirmLoading={loading}
    >
      <div style={{ marginBottom: 16 }}>
        <h4>Selected Stock Items:</h4>
        <List
          size="small"
          bordered
          dataSource={selectedStockItems}
          renderItem={(item) => (
            <List.Item>
              <Space>
                <Tag>{item?.receiptCustomRefNumber || "N/A"}</Tag>
                <span>{item?.stockTypeName || "N/A"}</span>
                <Tag color="blue">Qty: {item?.issuedQuantity || 0}</Tag>
              </Space>
            </List.Item>
          )}
          style={{ maxHeight: 200, overflowY: "auto" }}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <h4>Upload Documents:</h4>
        <Input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          style={{ marginBottom: 12 }}
        />
        {uploadedFiles.length > 0 && (
          <List
            size="small"
            bordered
            dataSource={uploadedFiles}
            renderItem={(file, index) => (
              <List.Item
                actions={[
                  <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => handleRemoveFile(index)}
                  >
                    Remove
                  </Button>,
                ]}
              >
                {file.name}
              </List.Item>
            )}
          />
        )}
      </div>
    </Modal>
  );
};

export default WriteOffModal;

